export type Branch = "human" | "pet";
export type Signal = "yes" | "no" | "tbd";
export type RouteStatus = "ready" | "needs-answers";

export type ArchitectureId =
  | "memorial-essential"
  | "celebration-of-life"
  | "memorial-tribute"
  | "multi-service-memorial"
  | "hybrid-remote-memorial"
  | "pet-memorial-essential"
  | "pet-life-story"
  | "pet-memorial-gallery"
  | "pet-continuing-remembrance";

export type FieldState<T> =
  | { status: "provided"; value: T }
  | { status: "tbd"; value?: undefined };

export type RoutingSignals = {
  branch: Branch | "tbd";
  multipleServices: Signal;
  remoteParticipation: Signal;
  continuingMemorial: Signal;
  expandedStory: Signal;
  photoLed: Signal;
};

export type RouteResult = {
  status: RouteStatus;
  branch: Branch | null;
  architecture: ArchitectureId | null;
  matchedSignals: string[];
  blockingQuestions: string[];
};

export type SiteId =
  | "memorandum"
  | "funeral-memorials"
  | "memorial-essential-demo"
  | "celebration-of-life-demo"
  | "memorial-tribute-demo"
  | "multi-service-memorial-demo"
  | "hybrid-memorial-demo"
  | "pet-memorials"
  | "pet-memorial-demo"
  | "pet-memorial-essential-demo"
  | "pet-life-story-demo"
  | "pet-memorial-gallery-demo"
  | "pet-continuing-remembrance-demo"
  | "memorandum-intake";

export type ProjectSetup = {
  workingProjectName: FieldState<string>;
  clientContactContext: FieldState<string>;
};

export type EventEntry = {
  name: FieldState<string>;
  date: FieldState<string>;
  time: FieldState<string>;
  venue: FieldState<string>;
  arrivalGuidance: FieldState<string>;
  remoteAccess: FieldState<string>;
};

export type PersonEntry = {
  name: FieldState<string>;
  role: FieldState<string>;
  relationship: FieldState<string>;
};

export type GuestExperience = {
  rsvp: FieldState<string>;
  accessibility: FieldState<string>;
  parking: FieldState<string>;
  transportation: FieldState<string>;
  lodging: FieldState<string>;
  livestream: FieldState<string>;
  condolences: FieldState<string>;
  donations: FieldState<string>;
};

export type MemorialContent = {
  biographySource: FieldState<string>;
  lifeStory: FieldState<string>;
  approvedTerminology: FieldState<string>;
  timeline: FieldState<string>;
  careerAndCommunity: FieldState<string>;
  storiesAndQuotes: FieldState<string>;
  photographs: FieldState<string>;
  memorialMessages: FieldState<string>;
};

export type CulturePrivacyAccessibility = {
  ceremonialContent: FieldState<string>;
  culturalContext: FieldState<string>;
  languageRequirements: FieldState<string>;
  translationSource: FieldState<string>;
  privacyRequirements: FieldState<string>;
  restrictedInformation: FieldState<string>;
  accessibilityNeeds: FieldState<string>;
  publicationRestrictions: FieldState<string>;
};

export type IntakeDocument = {
  schemaVersion: number;
  draftId: string;
  projectSetup: ProjectSetup;
  routing: RoutingSignals;
  events: EventEntry[];
  people: PersonEntry[];
  guestExperience: GuestExperience;
  memorialContent: MemorialContent;
  culturePrivacyAccessibility: CulturePrivacyAccessibility;
};

export const ARCHITECTURE_LABELS: Record<ArchitectureId, string> = {
  "memorial-essential": "Memorial Essential",
  "celebration-of-life": "Celebration of Life",
  "memorial-tribute": "Memorial Tribute",
  "multi-service-memorial": "Multi-Service Memorial",
  "hybrid-remote-memorial": "Hybrid / Remote Memorial",
  "pet-memorial-essential": "Pet Memorial Essential",
  "pet-life-story": "Life & Story Tribute",
  "pet-memorial-gallery": "Memorial Gallery",
  "pet-continuing-remembrance": "Continuing Remembrance"
};

const humanRules: Array<[keyof RoutingSignals, ArchitectureId, string, string]> = [
  ["multipleServices", "multi-service-memorial", "multiple-services", "Confirm whether more than one coordinated event is required."],
  ["remoteParticipation", "hybrid-remote-memorial", "remote-participation", "Confirm whether remote participation is materially important."],
  ["continuingMemorial", "memorial-tribute", "continuing-remembrance", "Confirm whether the site should remain active beyond immediate services."],
  ["expandedStory", "celebration-of-life", "expanded-story", "Confirm whether expanded storytelling is desired." ]
];

const petRules: Array<[keyof RoutingSignals, ArchitectureId, string, string]> = [
  ["continuingMemorial", "pet-continuing-remembrance", "continuing-remembrance", "Confirm whether the tribute should support continuing remembrance."],
  ["photoLed", "pet-memorial-gallery", "photo-led", "Confirm whether photography should be the primary storytelling medium."],
  ["expandedStory", "pet-life-story", "expanded-story", "Confirm whether an expanded life-and-story tribute is desired."]
];

export function matchMemorialArchitecture(signals: RoutingSignals): RouteResult {
  if (signals.branch === "tbd") {
    return { status: "needs-answers", branch: null, architecture: null, matchedSignals: [], blockingQuestions: ["Confirm whether this is a human or pet memorial."] };
  }

  const rules = signals.branch === "human" ? humanRules : petRules;
  for (const [signalKey, architecture, matchedSignal, question] of rules) {
    const value = signals[signalKey];
    if (value === "tbd") {
      return { status: "needs-answers", branch: signals.branch, architecture: null, matchedSignals: [], blockingQuestions: [question] };
    }
    if (value === "yes") {
      return { status: "ready", branch: signals.branch, architecture, matchedSignals: [matchedSignal], blockingQuestions: [] };
    }
  }

  return {
    status: "ready",
    branch: signals.branch,
    architecture: signals.branch === "human" ? "memorial-essential" : "pet-memorial-essential",
    matchedSignals: ["focused-remembrance"],
    blockingQuestions: []
  };
}

export const INTAKE_FIELD_LABELS: Record<string, string> = {
  "projectSetup.workingProjectName": "Working project name",
  "projectSetup.clientContactContext": "Client contact context",
  "routing.branch": "Memorial branch",
  "routing.multipleServices": "Multiple coordinated services",
  "routing.remoteParticipation": "Material remote participation",
  "routing.continuingMemorial": "Continuing memorial",
  "routing.expandedStory": "Expanded storytelling",
  "routing.photoLed": "Photography-led storytelling",
  "events.0.name": "Primary event name",
  "events.0.date": "Primary event date",
  "events.0.time": "Primary event time",
  "events.0.venue": "Primary event venue",
  "events.0.arrivalGuidance": "Primary event arrival guidance",
  "events.0.remoteAccess": "Primary event remote access",
  "people.0.name": "Primary participant name",
  "people.0.role": "Primary participant role",
  "people.0.relationship": "Primary participant relationship",
  "guestExperience.rsvp": "RSVP requirements",
  "guestExperience.accessibility": "Guest accessibility",
  "guestExperience.parking": "Parking",
  "guestExperience.transportation": "Transportation",
  "guestExperience.lodging": "Lodging",
  "guestExperience.livestream": "Livestream",
  "guestExperience.condolences": "Condolence instructions",
  "guestExperience.donations": "Donations or external resources",
  "memorialContent.biographySource": "Biography source",
  "memorialContent.lifeStory": "Life story",
  "memorialContent.approvedTerminology": "Approved terminology",
  "memorialContent.timeline": "Timeline",
  "memorialContent.careerAndCommunity": "Career and community information",
  "memorialContent.storiesAndQuotes": "Stories and quotations",
  "memorialContent.photographs": "Photographs",
  "memorialContent.memorialMessages": "Memorial messages",
  "culturePrivacyAccessibility.ceremonialContent": "Ceremonial content supplied by client",
  "culturePrivacyAccessibility.culturalContext": "Cultural or heritage context",
  "culturePrivacyAccessibility.languageRequirements": "Language requirements",
  "culturePrivacyAccessibility.translationSource": "Translation source",
  "culturePrivacyAccessibility.privacyRequirements": "Privacy requirements",
  "culturePrivacyAccessibility.restrictedInformation": "Restricted information",
  "culturePrivacyAccessibility.accessibilityNeeds": "Accessibility needs",
  "culturePrivacyAccessibility.publicationRestrictions": "Publication restrictions"
};
